import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Calendar, User } from 'lucide-react';

const Article1 = () => {
  const location = useLocation();

  useEffect(() => {
    // Handle scroll to hash anchor
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        // Wait a bit for the page to fully render
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  return (
    <div className="pt-16 sm:pt-20">
      {/* Header Section */}
      <section className="bg-gradient-to-br from-saffron-50 to-cream-100 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Articles & Resources</h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Discover insights, tips, and stories about premium FMCG products and traditional Indian agriculture.
            </p>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Article Header */}
          <article className="bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Featured Image */}
            <div className="h-96 overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1760982685244-17a1814ab806"
                alt="Pure Kashmiri Kesar"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Article Content */}
            <div className="p-8 md:p-12">
              {/* Meta Information */}
              <div className="flex items-center gap-6 text-sm text-gray-500 mb-6">
                <div className="flex items-center gap-2">
                  <Calendar size={16} />
                  <span>December 2024</span>
                </div>
                <div className="flex items-center gap-2">
                  <User size={16} />
                  <span>Hind Harvest Team</span>
                </div>
              </div>

              {/* Title */}
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Pure Kashmiri Kesar: A Clinically Proven Elixir of Wellness
              </h1>

              {/* Introduction */}
              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  For centuries, pure Kashmiri kesar has held a revered place in Ayurveda, Persian medicine, and modern pharmacological research. Cultivated exclusively in the pristine high-altitude regions of Kashmir, this rare saffron variety is distinguished by its deep red stigmas, high crocin content, and unmatched therapeutic potency.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Today, modern clinical science validates what tradition long believed—Kashmiri kesar is a powerful, evidence-backed wellness ingredient with measurable effects on mental, cardiovascular, hormonal, and cellular health.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-8">
                  Below is an in-depth, science-led exploration of its seven most important clinically proven health benefits, supported by peer-reviewed human studies.
                </p>

                {/* Benefit 1 - Happier Mind */}
                <div id="happier-mind" className="scroll-mt-24 mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Happier Mind, Naturally</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Clinical research has firmly established saffron as a natural antidepressant. Randomized, double-blind clinical trials published in <em>BMC Complementary Medicine</em> demonstrated that saffron extract significantly reduces symptoms of mild-to-moderate depression, performing comparably to standard pharmaceutical antidepressants such as imipramine and fluoxetine.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    The mechanism lies in saffron's bioactive compounds—crocin and safranal—which regulate key neurotransmitters including serotonin, dopamine, and norepinephrine. These compounds inhibit serotonin reuptake while simultaneously reducing oxidative stress and neuroinflammation, two biological drivers strongly linked to depressive disorders. Unlike synthetic antidepressants, saffron achieves mood stabilization without sedation or dependency, making it a clinically validated, natural solution for emotional well-being.
                  </p>
                </div>

                {/* Benefit 2 - Heart Health */}
                <div id="heart-health" className="scroll-mt-24 mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Heart-Healthy Protection</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Cardiovascular disease remains one of the most serious global health concerns, and saffron's cardioprotective properties are among its most clinically significant benefits. Human studies published in the <em>Indian Journal of Medical Sciences</em> confirm that saffron supplementation reduces oxidative modification of LDL cholesterol—one of the primary contributors to atherosclerosis.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Crocin acts as a powerful antioxidant that prevents lipid peroxidation within blood vessels, while saffron's anti-inflammatory action improves endothelial function. Together, these effects enhance blood flow, reduce arterial stiffness, and support long-term vascular health. By targeting oxidative stress at the molecular level, Kashmiri kesar provides meaningful protection against heart disease progression.
                  </p>
                </div>

                {/* Benefit 3 - Sharper Memory */}
                <div id="sharper-memory" className="scroll-mt-24 mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Sharper Memory & Focus</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Saffron is one of the few botanicals with clinical evidence supporting cognitive enhancement. Controlled human trials published in the <em>Journal of Alzheimer's Disease</em> demonstrate that saffron improves memory, learning capacity, and cognitive performance, even in individuals with age-related cognitive decline.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    This effect is attributed to crocin's ability to inhibit beta-amyloid plaque formation in the brain—a key pathological marker in neurodegenerative conditions. Additionally, saffron improves cerebral blood flow and protects neurons from oxidative damage. These mechanisms collectively support sharper focus, better recall, and sustained brain health, making kesar a scientifically validated neuroprotective agent.
                  </p>
                </div>

                {/* Benefit 4 - Stress Relief */}
                <div id="stress-relief" className="scroll-mt-24 mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Stress & Anxiety Relief</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Chronic stress and anxiety disrupt hormonal balance and increase systemic inflammation. Clinical studies published in <em>Human Psychopharmacology</em> confirm that saffron significantly reduces anxiety symptoms and perceived stress levels in adults.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Safranal modulates gamma-aminobutyric acid (GABA) and serotonin pathways, producing a calming effect on the central nervous system. Simultaneously, saffron lowers cortisol levels and reduces oxidative stress markers associated with chronic anxiety. This dual action—neurochemical balance and antioxidant defense—explains saffron's clinically proven role in promoting calm, emotional resilience, and mental clarity.
                  </p>
                </div>

                {/* Benefit 5 - PMS Comfort */}
                <div id="pms-comfort" className="scroll-mt-24 mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">PMS & Menstrual Comfort</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Among women's health benefits, saffron's effectiveness in relieving premenstrual syndrome (PMS) is particularly well-documented. A randomized controlled trial published in <em>BJOG (British Journal of Obstetrics and Gynaecology)</em> found that saffron significantly reduces PMS symptoms including cramps, mood swings, irritability, and fatigue.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    The therapeutic effect arises from saffron's influence on serotonin regulation and its antispasmodic properties, which help relax uterine muscles. Additionally, its anti-inflammatory action reduces prostaglandin activity, the biochemical trigger for menstrual pain. These clinically validated mechanisms position Kashmiri kesar as a natural, effective solution for menstrual comfort.
                  </p>
                </div>

                {/* Benefit 6 - Immunity */}
                <div id="immunity-support" className="scroll-mt-24 mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Stronger Immunity Support</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Saffron's immune-supporting benefits are rooted in its exceptionally high antioxidant capacity. Studies published in <em>Phytotherapy Research</em> confirm saffron's strong anti-inflammatory and immunomodulatory effects.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Crocin and crocetin neutralize free radicals, reduce inflammatory cytokines, and protect immune cells from oxidative damage. This strengthens the body's innate defense system and enhances resilience against infections, seasonal illnesses, and chronic inflammatory conditions. Rather than overstimulating immunity, saffron supports balanced immune regulation—an important marker of long-term health.
                  </p>
                </div>

                {/* Benefit 7 - Radiant Skin */}
                <div id="radiant-skin" className="scroll-mt-24 mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Radiant Skin From Within</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Healthy skin is a reflection of cellular health, and saffron's dermatological benefits are deeply tied to its antioxidant action. Clinical research demonstrates that saffron protects skin cells from oxidative stress, one of the primary causes of premature aging, pigmentation, and dullness.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    By neutralizing free radicals and improving microcirculation, saffron supports cellular repair, collagen preservation, and even skin tone. Its systemic anti-inflammatory effects further reduce skin sensitivity and oxidative damage, delivering visible radiance through internal nourishment rather than topical intervention alone.
                  </p>
                </div>

                {/* Clinical References */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Clinical References</h3>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li>Akhondzadeh et al., <em>BMC Complementary Medicine</em>, 2005 – Saffron vs. imipramine for depression</li>
                    <li>Lopresti & Drummond, <em>Human Psychopharmacology</em>, 2014 – Mood, anxiety, and stress modulation</li>
                    <li>Tsolaki et al., <em>Journal of Alzheimer's Disease</em>, 2016 – Cognitive and memory support</li>
                    <li>Fadai et al., <em>BJOG</em>, 2013 – PMS symptom reduction</li>
                    <li>Verma & Bordia, <em>Indian Journal of Medical Sciences</em>, 1998 – Cardiovascular antioxidant effects</li>
                    <li>Hosseinzadeh et al., <em>Phytotherapy Research</em>, 2009 – Anti-inflammatory and immune mechanisms</li>
                  </ul>
                </div>
              </div>
            </div>
          </article>

          {/* More Articles Coming Soon */}
          <div className="mt-12 bg-gradient-to-br from-saffron-50 to-cream-100 rounded-2xl p-8 text-center">
            <p className="text-lg font-semibold text-gray-900 mb-2">More Articles Coming Soon</p>
            <p className="text-gray-600">
              Stay tuned for more insightful articles about traditional farming practices, health benefits, recipes, and India's rich agricultural heritage.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Article1;
